// src/affinity_crm_api.js
import { supabase, isConfigured } from "./affinity_accounting_supabase";

// ─────────────────────────────────────────────────────────────────────────────
// THE CALL HELPER. This file used call() throughout and never defined it, so
// every function in it threw "call is not defined" the moment it ran. Reported
// as "CRM & Onboarding are not working" with exactly that message in the stack.
//
// It compiled, because an undefined identifier in JavaScript is only an error
// when it is reached. It rendered, because the module renders before it loads.
// It failed the first time anybody opened the screen.
//
// Five API files were in this state. The working ones each define their own
// helper; these five were written expecting one and never got it.
// ─────────────────────────────────────────────────────────────────────────────
const clean = (m) => String(m || "").replace(/^[A-Z0-9]{5}:\s*/, "");

async function call(fn, args) {
  if (!isConfigured) {
    return { ok: false, live: false, data: null,
             error: "Not signed in — this is read from the database." };
  }
  try {
    const { data, error } = await supabase.rpc(fn, args || {});
    if (error) return { ok: false, live: true, data: null, error: clean(error.message) };
    return { ok: true, live: true, data, error: null };
  } catch (e) {
    return { ok: false, live: false, data: null, error: String((e && e.message) || e) };
  }
}

const off = () => ({ data: null, error: new Error("not configured") });
// ── CRM pipeline (db/084) ───────────────────────────────────────────────────
// Prospects and their contact log had no table until db/084.
//
// Stages are validated against a list — Enquiry, Proposal Sent, KYC Arriving,
// Fees Paid, Lost — so a typo cannot create a stage nothing reports on.
export const crmProspects = (openOnly) =>
  call("crm_prospects", { p_open_only: openOnly !== false });
export const crmInteractions = (prospectId, limit) =>
  call("crm_interactions", { p_prospect: prospectId ?? null, p_limit: limit || 200 });
export const crmProspectAdd = (p) => call("crm_prospect_add", { p });
// Marking a prospect lost requires a reason — why we lost it is the useful
// part of a lost prospect.
export const crmStageSet = (id, stage, lostReason) =>
  call("crm_stage_set", { p_id: id, p_stage: stage, p_lost_reason: lostReason || null });
export const crmInteractionAdd = (i) => call("crm_interaction_add", {
  p_prospect: i.prospectId, p_date: i.date, p_type: i.type, p_note: i.note || null,
  p_next_action: i.nextAction || null, p_next_due: i.nextDue || null,
});
// Converts a won prospect into an onboarding case and links the two, so the
// pipeline and the onboarding file are one story rather than two records of
// the same client.
export const crmProspectConvert = (id) => call("crm_prospect_convert", { p_id: id });
